import ApartmentIcon from '@mui/icons-material/Apartment';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import FeedIcon from '@mui/icons-material/Feed';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { Box, IconButton, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import { SetStateAction, useState } from 'react';

import DescriptionModal from '..';
import { EnabledKeys, InstanceInfo } from '../../../../Types/AuthServer';

const pluginCardData = [
    {
        description:
            'Keep users informed with the Activity Log feature. Receive timely notifications about system updates, messages, and changes to settings that directly impact them. This comprehensive log provides a transparent view of relevant activities, ensuring users stay in the loop and can easily track important events within the app. Stay connected and well-informed with the Activity Log.',
        icon: <ChatIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'activitylog_enabled',
        name: 'Activity Log',
        tagline: 'Get notifications with Activity Log.',
    },
    {
        description:
            "Enable seamless group interaction. Members can join public groups or receive invitations to private groups. Within these groups, users gain visibility into each other's activities, facilitating a professional and collaborative environment. Stay informed about members' presence and activity status, improving communication and coordination within the application.",
        icon: <GroupIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'groups_enabled',
        name: 'Groups',
        tagline: 'Allow group feature in Renki.',
    },
    {
        description:
            'Optimise workplace visibility with the Companies feature. Tailored for businesses, users can connect within dedicated company spaces. Companies provide a platform for members to monitor the presence of their colleagues in the workplace and track their recent activity. Improve collaboration and promote transparency within your organisation, allowing members to stay informed about team dynamics and site engagement.',
        icon: <ApartmentIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'company_enabled',
        name: 'Companies',
        tagline: 'Allow company feature in Renki.',
    },
    {
        description:
            'Enable dynamic office communication with the Infoscreen feature. Customised screens display members of specific groups or companies, providing real-time visibility of presence on site. Ideal for displaying on office TVs, Infoscreens keep teams informed of who is currently on site. Improve workplace transparency and streamline communication with this personalised and informative feature.',
        icon: <DashboardIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'dashboard_enabled',
        name: 'Infoscreen',
        tagline: 'Allow infoscreen feature in Renki.',
    },
    {
        description:
            'Introduce scheduled interactions with the Events feature. Users can create and join events for specific times, providing a structured framework for meetings, announcements or special occasions. Improve real-time collaboration within your application by providing a dedicated space for time-sensitive engagements.',
        icon: <EventIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'events_enabled',
        name: 'Events',
        tagline: 'Allow event feature in Renki.',
    },
    {
        description:
            "Use data insights with the Graphs feature. Designed to count people flow or track active devices, Graphs provide a visual representation of key metrics. Analyse trends, monitor activity and make informed decisions based on real-time data. Enhance your app's capabilities by integrating Graphs to quantify and understand user engagement or device activity.",
        icon: <BarChartIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'graphs_enabled',
        name: 'Graphs',
        tagline: 'Allow graph feature in Renki.',
    },
    {
        description:
            'Enable users to access personalized insights through Info Cards. When enabled, users can view a snapshot of their on-site presence, weekly on-site hours, and weekly remote hours. This feature provides a valuable tool for self-monitoring and assessing engagement.',
        icon: <WorkHistoryIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'infocard_enabled',
        name: 'Info Cards',
        tagline: 'Display info cards in Renki.',
    },
    {
        description:
            "Improve scheduling and visibility with the calendar feature. Users can display their presence on a calendar, providing a clear overview of their availability. Enable calendar sharing for a collaborative experience, allowing users to view each other's schedules with permission. Streamline coordination and empower users to plan effectively with this intuitive calendar integration.",
        icon: <CalendarTodayIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'calendar_enabled',
        name: 'Calendar',
        tagline: 'Allow calendar feature in Renki.',
    },
    {
        description:
            'Unlock exclusive benefits with the Benefits feature. Give users access to discounts at specific locations and other valuable offers. Increase user satisfaction by offering special benefits to create a more rewarding experience within your app.',
        icon: <LoyaltyIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'benefits_enabled',
        name: 'Benefits',
        tagline: 'Allow benefits feature in Renki.',
    },
    {
        description:
            'Stay connected with the Publishers feature. Users can follow publishers and receive their messages directly in their Activity Log. Stay up to date with important announcements and content from preferred sources. Improve communication and information flow by customising your experience with the Publishers feature.',
        icon: <FeedIcon color="primary" sx={{ height: '100%', width: '100%' }} />,
        key: 'publishers_enabled',
        name: 'Publishers',
        tagline: 'Allow publishers feature in Renki.',
    },
];

function getEnabledKeys(object: InstanceInfo | undefined) {
    const filteredObject: Partial<InstanceInfo> = {};

    if (object) {
        for (const key of Object.keys(object) as EnabledKeys[]) {
            if (key.includes('_enabled')) {
                filteredObject[key] = object[key];
            }
        }
    }

    return filteredObject as InstanceInfo;
}

interface FeatureTabProps {
    features: InstanceInfo;
}

export default function FeatureTab({ features }: Readonly<FeatureTabProps>) {
    const [modalData, setModalData] = useState({ name: '' });
    const [open, setOpen] = useState(false);
    const handleOpen = (e: SetStateAction<{ name: string }>) => {
        setOpen(true);
        setModalData(e);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const instanceInfoPlugins = getEnabledKeys(features);
    const cardData = pluginCardData.find((item) => item.key === modalData.name);

    return Object.entries(instanceInfoPlugins).length > 0 ? (
        <Box>
            <List disablePadding sx={{ bgcolor: 'background.paper', width: '100%' }}>
                {Object.entries(instanceInfoPlugins).map(([itemKey, value]) => (
                    <ListItem
                        disablePadding
                        key={itemKey}
                        secondaryAction={
                            <IconButton edge="end" onClick={() => handleOpen({ name: itemKey })}>
                                <InfoIcon color="primary" />
                            </IconButton>
                        }
                    >
                        <ListItemAvatar
                            sx={{
                                alignItems: 'center',
                                bgcolor: 'white',
                                display: 'flex',
                                height: 24,
                                justifyContent: 'center',
                                width: 24,
                            }}
                        >
                            {pluginCardData.find((item) => item.key === itemKey)?.icon}
                        </ListItemAvatar>
                        <ListItemText
                            primary={pluginCardData.find((item) => item.key === itemKey)?.name}
                            secondary={value ? 'Enabled' : 'Disabled'}
                            slotProps={{
                                secondary: { color: value ? 'success.main' : 'error.main' },
                            }}
                        />
                    </ListItem>
                ))}
            </List>
            <DescriptionModal
                icon={cardData?.icon}
                instanceDescription={{ description: cardData?.description, name: cardData?.name }}
                onClose={handleClose}
                open={open}
            />
        </Box>
    ) : (
        <Box>
            <Typography>No features active</Typography>
        </Box>
    );
}
